import React, { useState, useEffect } from "react";
import Select from 'react-select';
import Modal from "../Modal";
import Button from "../Button";
import api from "../../services/api";
import { renderError } from "../../helpers/errors";
import { createOptions } from "../../helpers/selects";
import { toast } from "react-toastify";
import { hasPlanFeature } from "../../services/auth";

import Icon from "../Icon";

const ICON_MAP = {
    edit: 'edit',
    filter: 'filter',
    check: 'check',
    calendar: 'calendar',
    copy: 'tag',
    table: 'table',
    'plus-blue': 'minimum',
    'save-blue': 'destaques',
};

/**
 * valueType define o comportamento do step 2:
 *   'multirelation' → modo (manter/substituir) + multiselect
 *   'boolean'       → radio Sim/Não (ou Ativo/Inativo)
 *   'singleselect'  → select simples (ex: tag)
 *   'integer'       → input numérico
 */
const ATTRIBUTE_TYPES = [
    {
        key: 'customizations',
        label: 'Gravações',
        description: 'Editar as gravações vinculadas aos produtos',
        icon: 'edit',
        valueType: 'multirelation',
    },
    {
        key: 'categories',
        label: 'Categorias',
        description: 'Editar as categorias vinculadas aos produtos',
        icon: 'filter',
        valueType: 'multirelation',
    },
    {
        key: 'main_site',
        label: 'Site principal',
        description: 'Mostrar ou tirar os produtos do site principal (não muda as marcas)',
        icon: 'eye',
        valueType: 'boolean',
        booleanOptions: [
            { value: 1, label: 'Mostrar no site principal' },
            { value: 0, label: 'Tirar do site principal' },
        ],
    },
    {
        key: 'quotable',
        label: 'Orçamentos pelo sistema',
        description: 'Mostrar ou esconder os produtos na busca ao montar um orçamento',
        icon: 'check',
        valueType: 'boolean',
        booleanOptions: [
            { value: 1, label: 'Aparece nos orçamentos' },
            { value: 0, label: 'Não aparece nos orçamentos' },
        ],
    },
    {
        key: 'featured',
        label: 'Destaque',
        description: 'Marcar como produto em destaque',
        icon: 'save-blue',
        valueType: 'boolean',
        booleanOptions: [
            { value: 1, label: 'Sim, em destaque' },
            { value: 0, label: 'Não' },
        ],
    },
    {
        key: 'recent',
        label: 'Novidade',
        description: 'Marcar como produto novo/recente',
        icon: 'calendar',
        valueType: 'boolean',
        booleanOptions: [
            { value: 1, label: 'Sim, é novidade' },
            { value: 0, label: 'Não' },
        ],
    },
    {
        key: 'tag_id',
        label: 'Selo',
        description: 'Definir o selo (tag) dos produtos',
        icon: 'copy',
        valueType: 'singleselect',
    },
    {
        key: 'min_quantity',
        label: 'Qtd. mínima',
        description: 'Definir quantidade mínima de compra',
        icon: 'plus-blue',
        valueType: 'integer',
    },
];

const MODES = [
    {
        key: 'merge',
        label: 'Manter existentes e adicionar',
        description: 'Os dados atuais são mantidos e os selecionados abaixo são adicionados',
    },
    {
        key: 'replace',
        label: 'Substituir',
        description: 'Os dados atuais são removidos e apenas os selecionados abaixo serão usados',
    },
];

const selectStyles = {
    control: (provided) => ({
        ...provided,
        fontSize: '14px',
        minHeight: '38px',
    }),
    menu: (provided) => ({
        ...provided,
        zIndex: 9999,
    }),
};

export const ModalBulkEditAttributes = ({ show, close, selectedIds, refresh }) => {
    const visibleAttributeTypes = ATTRIBUTE_TYPES.filter(type => {
        if (type.key === 'customizations') {
            return hasPlanFeature('calculation');
        }

        return true;
    });

    const [step, setStep] = useState(1);
    const [selectedType, setSelectedType] = useState(null);

    // multirelation
    const [selectedMode, setSelectedMode] = useState('merge');
    const [selectedValues, setSelectedValues] = useState([]);

    // boolean / singleselect / integer
    const [scalarValue, setScalarValue] = useState(null);

    // dados externos
    const [categories, setCategories] = useState([]);
    const [customizations, setCustomizations] = useState([]);
    const [tags, setTags] = useState([]);
    const [loadingData, setLoadingData] = useState(false);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (show) {
            resetForm();
            loadData();
        }
    }, [show]);

    const resetForm = () => {
        setStep(1);
        setSelectedType(null);
        setSelectedMode('merge');
        setSelectedValues([]);
        setScalarValue(null);
    };

    const loadData = () => {
        setLoadingData(true);
        Promise.all([
            api.get('/category'),
            api.get('/customization'),
            api.get('/tag'),
        ]).then(([catRes, custRes, tagRes]) => {
            const flat = [];
            catRes.data.resources.forEach(category => {
                flat.push(category);
                category.categories.forEach(sub => flat.push({
                    ...sub,
                    name: `${category.name} > ${sub.name}`,
                }));
            });
            setCategories(flat);
            setCustomizations(custRes.data.resources);
            setTags(tagRes.data.resources);
        }).catch(renderError).finally(() => setLoadingData(false));
    };


    

    const handleSelectType = (typeKey) => {
        setSelectedType(typeKey);
        // inicializa o valor escalar conforme o tipo
        const type = ATTRIBUTE_TYPES.find(t => t.key === typeKey);
        if (type?.valueType === 'boolean') {
            setScalarValue(type.booleanOptions[0].value);
        } else if (type?.valueType === 'integer') {
            setScalarValue('');
        } else {
            setScalarValue(null);
        }
        setSelectedValues([]);
    };

    const handleContinue = () => {
        if (!selectedType) return;
        setStep(2);
    };

    const handleBack = () => {
        setStep(1);
        setSelectedValues([]);
        setScalarValue(null);
    };

    const isSubmitDisabled = () => {
        if (!currentType) return true;
        switch (currentType.valueType) {
            case 'multirelation': return selectedValues.length === 0;
            case 'integer': return scalarValue === '' || scalarValue === null || isNaN(Number(scalarValue));
            default: return false;
        }
    };

    const buildPayload = () => {
        const base = { ids: selectedIds, type: selectedType };
        switch (currentType.valueType) {
            case 'multirelation':
                return { ...base, mode: selectedMode, value_ids: selectedValues.map(v => v.value) };
            case 'boolean':
                return { ...base, value: scalarValue };
            case 'singleselect':
                return { ...base, value: scalarValue?.value ?? null };
            case 'integer':
                return { ...base, value: parseInt(scalarValue, 10) };
            default:
                return base;
        }
    };

    const handleSubmit = () => {
        if (isSubmitDisabled()) {
            toast.error('Preencha o valor para continuar.');
            return;
        }

        setLoading(true);
        api.patch('/product/bulk', buildPayload())
            .then(() => {
                toast.success('Produtos atualizados com sucesso!');
                if (refresh) refresh();
                close();
            })
            .catch(renderError)
            .finally(() => setLoading(false));
    };

    const currentType = ATTRIBUTE_TYPES.find(t => t.key === selectedType);

    const getMultiSelectOptions = () => {
        if (selectedType === 'categories') return createOptions(categories, 'name', 'id');
        if (selectedType === 'customizations') return createOptions(customizations, 'name', 'id');
        return [];
    };

    const getTagOptions = () => [
        { label: 'Nenhum selo', value: null },
        ...createOptions(tags, 'text', 'id'),
    ];

    if (selectedIds.length === 0) {
        return (
            <Modal
                show={show}
                close={close}
                title="Editar atributos em massa (0 produtos)"
                footer={
                    <Button
                        type="primary"
                        size="small"
                        text="Entendi"
                        action={close}
                    />
                }
            >
                    <div  className={`no-padding section`} style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                        <div className="deleted-product-modal">
                            <div className="deleted-product-content">
                                <Icon name="octagon-alert" className="deleted-product-icon" size={48} color="#F59E0B" />

                                <h3 className="deleted-product-title">Nenhum Produto Selecionado</h3>
                                <p className="deleted-product-description">Selecione os produtos que deseja editar em massa antes de continuar.</p>
                            </div>
                        </div>
                    </div>
                </Modal>
        );
    }

    return (
        <Modal
            show={show}
            close={close}
            title={`Editar atributos em massa (${selectedIds.length} ${selectedIds.length === 1 ? 'produto' : 'produtos'})`}
            footer={
                step === 1 ? (
                    <>
                        <Button type="secondary" size="small" text="Cancelar" action={close} />
                        <Button
                            type="primary"
                            size="small"
                            text="Continuar"
                            action={handleContinue}
                            disabled={!selectedType}
                        />
                    </>
                ) : (
                    <>
                        <Button type="secondary" size="small" text="Voltar" action={handleBack} />
                        <Button
                            type="primary"
                            size="small"
                            text="Aplicar"
                            action={handleSubmit}
                            loading={loading}
                            disabled={isSubmitDisabled()}
                        />
                    </>
                )
            }
        >
            {step === 1 && (
                <div className="bulk-edit-step">
                    <p className="bulk-edit-label">O que deseja editar?</p>
                    <div className="bulk-edit-type-cards">
                        {visibleAttributeTypes.map(type => (
                            <button
                                key={type.key}
                                className={`bulk-edit-type-card${selectedType === type.key ? ' active' : ''}`}
                                onClick={() => handleSelectType(type.key)}
                            >
                                <Icon name={ICON_MAP[type.icon]} size={22} />
                                <strong>{type.label}</strong>
                                <span>{type.description}</span>
                            </button>
                        ))}
                    </div>
                </div>
            )}

            {step === 2 && currentType?.valueType === 'multirelation' && (
                <div className="bulk-edit-step">
                    <p className="bulk-edit-label">
                        O que fazer com as <strong>{currentType.label.toLowerCase()}</strong> atuais?
                    </p>
                    <div className="bulk-edit-modes">
                        {MODES.map(mode => (
                            <button
                                key={mode.key}
                                className={`bulk-edit-mode-option${selectedMode === mode.key ? ' active' : ''}`}
                                onClick={() => setSelectedMode(mode.key)}
                            >
                                <div className="bulk-edit-mode-radio" />
                                <div>
                                    <strong>{mode.label}</strong>
                                    <span>{mode.description}</span>
                                </div>
                            </button>
                        ))}
                    </div>
                    <p className="bulk-edit-label" style={{ marginTop: 20 }}>
                        {currentType.label} a aplicar:
                    </p>
                    <Select
                        isMulti
                        placeholder={`Selecione as ${currentType.label.toLowerCase()}...`}
                        options={getMultiSelectOptions()}
                        value={selectedValues}
                        onChange={setSelectedValues}
                        isLoading={loadingData}
                        noOptionsMessage={() => 'Nenhuma opção encontrada'}
                        loadingMessage={() => 'Carregando...'}
                        styles={selectStyles}
                    />
                </div>
            )}

            {step === 2 && currentType?.valueType === 'boolean' && (
                <div className="bulk-edit-step">
                    <p className="bulk-edit-label">
                        Qual será o novo valor de <strong>{currentType.label}</strong>?
                    </p>
                    <div className="bulk-edit-modes">
                        {currentType.booleanOptions.map(option => (
                            <button
                                key={option.value}
                                className={`bulk-edit-mode-option${scalarValue === option.value ? ' active' : ''}`}
                                onClick={() => setScalarValue(option.value)}
                            >
                                <div className="bulk-edit-mode-radio" />
                                <div>
                                    <strong>{option.label}</strong>
                                </div>
                            </button>
                        ))}
                    </div>
                </div>
            )}

            {step === 2 && currentType?.valueType === 'singleselect' && (
                <div className="bulk-edit-step">
                    <p className="bulk-edit-label">
                        Qual será o novo <strong>{currentType.label.toLowerCase()}</strong>?
                    </p>
                    <Select
                        placeholder={`Selecione o ${currentType.label.toLowerCase()}...`}
                        options={getTagOptions()}
                        value={scalarValue}
                        onChange={setScalarValue}
                        isLoading={loadingData}
                        noOptionsMessage={() => 'Nenhuma opção encontrada'}
                        loadingMessage={() => 'Carregando...'}
                        styles={selectStyles}
                    />
                </div>
            )}

            {step === 2 && currentType?.valueType === 'integer' && (
                <div className="bulk-edit-step">
                    <p className="bulk-edit-label">
                        Qual será a nova <strong>{currentType.label.toLowerCase()}</strong>?
                    </p>
                    <input
                        type="number"
                        min="0"
                        className="bulk-edit-number-input"
                        placeholder="Ex: 10"
                        value={scalarValue ?? ''}
                        onChange={e => setScalarValue(e.target.value)}
                    />
                </div>
            )}
        </Modal>
    );
};

export default ModalBulkEditAttributes;
